import React, { useState, useRef, useEffect } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { ShieldCheck, Languages, Car, Wallet, Siren, Gauge, Truck, BookOpen, LogOut, User, LayoutDashboard } from 'lucide-react';
import { useLang } from '../i18n';
import { useAuth } from '../auth';
import NotificationBell from './NotificationBell';
import ThemeSwitcher from './ThemeSwitcher';

const NAV = [
  { to: '/buy', key: 'nav_buy', icon: Car, tid: 'nav-buy', roles: ['customer', 'fleet_operator'] },
  { to: '/fleet', key: 'nav_fleet', icon: Truck, tid: 'nav-fleet', roles: ['fleet_operator', 'customer', 'admin'] },
  { to: '/carry', key: 'nav_carry', icon: Wallet, tid: 'nav-carry', roles: ['customer', 'fleet_operator'] },
  { to: '/claim', key: 'nav_claim', icon: Siren, tid: 'nav-claim', roles: ['customer', 'fleet_operator'] },
  { to: '/ops', key: 'nav_ops', icon: Gauge, tid: 'nav-ops', roles: ['adjuster', 'admin'] },
  { to: '/admin', key: 'nav_admin', icon: LayoutDashboard, tid: 'nav-admin', roles: ['admin'] },
  { to: '/docs', key: 'nav_docs', icon: BookOpen, tid: 'nav-docs', roles: ['customer', 'fleet_operator', 'adjuster', 'admin'] },
];

export default function Layout({ children }) {
  const { t, lang, setLang } = useLang();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [menu, setMenu] = useState(false);
  const ref = useRef(null);
  const role = user?.role;
  const items = NAV.filter((n) => !role || n.roles.includes(role));

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const isCustomerish = role === 'customer' || role === 'fleet_operator';

  return (
    <div className="grain min-h-screen flex flex-col">
      <header className="sticky top-0 z-30 glass border-b border-white/8">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="brand-logo">
            <div className="w-9 h-9 rounded-xl bg-jade/15 border border-jade/40 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-jade" />
            </div>
            <div className="leading-none hidden sm:block">
              <div className="font-display font-extrabold text-lg tracking-tight">{t('brand')}</div>
              <div className="kicker" style={{ fontSize: 9 }}>AI NATIVE</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
            {items.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={n.tid}
                className={({ isActive }) => `px-3 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${isActive ? 'text-jade bg-jade/10' : 'text-slate-300 hover:text-white'}`}>
                <n.icon className="w-4 h-4" /> {t(n.key)}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            {isCustomerish && <NotificationBell />}
            <ThemeSwitcher />
            <button onClick={() => setLang(lang === 'en' ? 'vi' : 'en')} data-testid="lang-toggle"
              className="chip px-3 py-1.5 text-xs font-mono flex items-center gap-2 text-slate-200">
              <Languages className="w-4 h-4" /> {lang === 'en' ? 'EN' : 'VI'}
            </button>
            <div className="relative" ref={ref}>
              <button onClick={() => setMenu(!menu)} data-testid="user-menu" className="chip px-2.5 py-1.5 text-slate-200 flex items-center gap-2">
                <User className="w-4 h-4" /> <span className="hidden sm:inline text-xs max-w-[90px] truncate">{user?.name}</span>
              </button>
              {menu && (
                <div className="absolute right-0 mt-2 w-52 glass rounded-2xl border border-white/10 p-2 z-50">
                  <div className="px-2 py-2">
                    <div className="text-sm font-medium truncate">{user?.name}</div>
                    <div className="text-xs text-slate-400 truncate">{user?.email}</div>
                    <div className="mt-1 kicker">{user?.role}</div>
                  </div>
                  <button onClick={() => { logout(); nav('/login'); }} data-testid="logout-btn"
                    className="w-full flex items-center gap-2 px-2 py-2 rounded-xl text-sm text-slate-300 hover:bg-white/5">
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 relative z-10">{children}</main>

      <nav className="lg:hidden sticky bottom-0 z-30 glass border-t border-white/8 flex overflow-x-auto">
        {items.map((n) => (
          <NavLink key={n.to} to={n.to}
            className={({ isActive }) => `flex-1 min-w-[64px] py-3 flex flex-col items-center gap-1 text-[10px] font-mono uppercase ${isActive ? 'text-jade' : 'text-slate-400'}`}>
            <n.icon className="w-5 h-5" /> {t(n.key)}
          </NavLink>
        ))}
      </nav>

      <footer className="relative z-10 border-t border-white/8 py-6 text-center text-xs text-slate-500 font-mono">
        Tasco AI Insurance — Prototype · <Link to="/docs" className="text-jade hover:underline">Documentation</Link>
      </footer>
    </div>
  );
}
